// Web Crypto APIを使用した暗号化/復号化ユーティリティ

/**
 * パスフレーズから暗号化キーを導出
 */
async function deriveKey(
  passphrase: string,
  salt: Uint8Array<ArrayBuffer>,
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passphraseKey = await crypto.subtle.importKey(
    "raw",
    encoder.encode(passphrase) as Uint8Array<ArrayBuffer>,
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt,
      iterations: 100000,
      hash: "SHA-256",
    },
    passphraseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * データを暗号化
 */
export async function encryptData(
  data: string,
  passphrase: string,
): Promise<string> {
  const encoder = new TextEncoder();
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);

  const encryptedContent = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv },
    key,
    encoder.encode(data) as Uint8Array<ArrayBuffer>,
  );

  // salt + iv + encryptedContent を結合してBase64エンコード
  const encryptedData = new Uint8Array(
    salt.length + iv.length + encryptedContent.byteLength,
  );
  encryptedData.set(salt, 0);
  encryptedData.set(iv, salt.length);
  encryptedData.set(new Uint8Array(encryptedContent), salt.length + iv.length);

  return btoa(String.fromCharCode(...encryptedData));
}

/**
 * データを復号化
 */
export async function decryptData(
  encryptedBase64: string,
  passphrase: string,
): Promise<string> {
  try {
    // Base64デコード
    const encryptedData = Uint8Array.from(atob(encryptedBase64), (c) =>
      c.charCodeAt(0),
    );

    // salt, iv, encryptedContentを分離
    const salt = encryptedData.slice(0, 16);
    const iv = encryptedData.slice(16, 28);
    const encryptedContent = encryptedData.slice(28);

    const key = await deriveKey(passphrase, salt);

    const decryptedContent = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: iv },
      key,
      encryptedContent,
    );

    const decoder = new TextDecoder();
    return decoder.decode(decryptedContent);
  } catch (error) {
    console.error("Decryption failed:", error);
    throw new Error("復号化に失敗しました。パスフレーズが正しくありません。");
  }
}
